import { useEffect, useState } from "react";
import ApiService from "../core/service/ApiService";
import { Button, Card, Divider, List, Table, Typography } from "antd";
import {
    CloudDownloadOutlined,
    FileImageOutlined,
    FilePdfOutlined, FolderOpenOutlined,
    LoadingOutlined,
    ShareAltOutlined
} from "@ant-design/icons";
import { t } from "i18next";
import { LocalStorageService } from "../core/service/LocalStorageService";
import PatientLocalStorage from "./PatientLocalStorage";
import { isMobile } from "react-device-detect";
import TokenDurationModal from "./TokenDurationModal";

export default function PatientProfileShare() {
    const [studies, setStudies] = useState([]);
    const patient = PatientLocalStorage.getInformation()
    const [activeIndex, setActiveIndex] = useState(null);

    const toggle = (index) => {
        setActiveIndex(activeIndex === index ? null : index);
    };

    const currentUrl = window.location.href;

    const isValidAccessToken = (token) => {
        if (!token) return false;

        try {
            const payload = JSON.parse(atob(token.split(".")[1]));
            const currentTime = Math.floor(Date.now() / 1000);
            return payload.exp > currentTime;
        } catch (e) {
            return false;
        }
    };

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const accessToken = params.get("access_token");

        if (!accessToken || !isValidAccessToken(accessToken)) {
            alert(t('The shared link has expired. Please ask the owner to share a new link.'));
            window.location.href = "/error";
        }

    }, []);

    const getReports = async () => {
        const res = await ApiService.PatientApp.GetReports();
        if (res.success) {
            // Group report rows into studies by studyId (NOT studyDate — distinct studies can share a date).
            // Local PATIENT_RESULT files have no studyId, so each one gets its own row keyed by fileId.
            const byStudy = new Map();
            for (const report of res.data) {
                const key = (report.studyId && report.studyId !== 0) ? `s_${report.studyId}` : `f_${report.fileId}`;
                if (!byStudy.has(key)) {
                    byStudy.set(key, { ...report, reports: [report] });
                } else {
                    byStudy.get(key).reports.push(report);
                }
            }
            setStudies([...byStudy.values()]);
        }
    }

    const shareOrCopy = async (title, text, url) => {
        if (navigator.share) {
            try { await navigator.share({ title, text, url }); }
            catch (e) { navigator.clipboard.writeText(url).then(() => alert(t('Link copied to clipboard'))); }
        } else {
            navigator.clipboard.writeText(url).then(() => alert(t('Link copied to clipboard')));
        }
    }

    const openRisPdf = async (item) => {
        const res = await ApiService.PatientApp.GetStudyAccess(item.studyId, 2);
        if (res.success) {
            const report = (res.data?.reports || []).find(r => r.reportId === item.reportId);
            if (report?.pdfUrl) window.open(report.pdfUrl, '_blank');
            else alert(t('Report not available'));
        }
    }

    const openPdf = async (fileId) => {
        const res = await ApiService.PatientApp.DownloadFile(fileId)
        if (res.success) {
            window.open(res.data, "_blank");
        }
    }

    // Download is proxied through HIS: the browser hits a HIS endpoint that streams the zip (DICOM + embedded
    // viewer) straight from RIS — same artifact as a "Share Study / Include Viewer" link, but never exposing RIS.
    const downloadZip = (item) => {
        const a = document.createElement('a');
        a.href = ApiService.PatientApp.GetStudyZipUrl(item.studyId);
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    }

    const openStudy = async (studyId) => {
        const res = await ApiService.PatientApp.GetStudyAccess(studyId, 2);
        if (res.success && res.data?.viewerUrl) window.open(res.data.viewerUrl);
    }

    const getOhifLink = async (item, tokenType = 2) => {
        const res = await ApiService.PatientApp.GetStudyAccess(item.studyId, tokenType);
        if (res.success && res.data?.viewerUrl) {
            await shareOrCopy(t('Study Viewer Link'), `${patient?.name} ${patient?.lastname}\n${item.studyDescription || ''}`, res.data.viewerUrl);
        }
    }

    const getLink = async (item, tokenType = 2) => {
        const res = await ApiService.PatientApp.GetStudyAccess(item.studyId, tokenType);
        if (res.success && res.data?.zipUrl) {
            await shareOrCopy(t('Study Link'), `${patient?.name} ${patient?.lastname}\n${item.studyDescription || ''}`, res.data.zipUrl);
        }
    }

    const getProfileLink = async (tokenType) => {
        const res = await ApiService.PatientApp.GetAccessToken(tokenType);
        if (res.success) {
            const longUrl = `${window?.AppSettings?.server?.api}/ProfileShare?access_token=${res.data}`;
            const shortRes = await ApiService.PatientApp.CreateUrl(longUrl);
            const url = (shortRes && shortRes.success)
                ? `${window?.AppSettings?.server?.api}/api/PatientApp/GetUrl/${shortRes.data}`
                : longUrl;
            await shareOrCopy(t('Study Viewer Link'), `${patient?.name} ${patient?.lastname}`, url);
        }
    }

    useEffect(() => {
        getReports();
    }, []);

    return (
        <div>
            <Card>
                {/* <h1>{patient?.name} {patient?.lastname}</h1> */}
                {/* <Divider /> */}
                <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'start', gap: 24, flexWrap: 'wrap' }}>
                    {/* <img src="/user.png" width={128} alt="Patient" style={{borderRadius: '50%', marginBottom: 16}}/> */}
                    <div style={{ minWidth: 400 }}>
                        <table>
                            {<tr>
                                <td colSpan={2}>
                                    <Typography.Title level={3}>{patient?.name} {patient?.lastname}</Typography.Title>
                                </td>
                            </tr>}
                            <tr>
                                <td>{t('Identity')}:</td>
                                <td>{patient?.customId}</td>
                            </tr>
                            <tr>
                                <td>{t('Birthdate')}:</td>
                                <td>{patient?.birthDay?.toString()?.padStart(2, '0')}/{patient?.birthMonth?.toString()?.padStart(2, '0')}/{patient?.birthYear}</td>
                            </tr>
                            <tr>
                                <td>{t('Gender')}:</td>
                                <td>{t(patient?.gender)}</td>
                            </tr>
                        </table>
                    </div>
                </div>
                <Typography.Text>
                    <br />
                    Sistemde kayıtlı rapor ve çekimlerinizi aşağıda listeleyebilirsiniz. Eksik veya hatalı bir bilgi varsa lütfen <b><i>"{t('Contact')}"</i></b> kısmından bize ulaşın.
                    <br />
                </Typography.Text>
            </Card>
            <div style={{ backgroundColor: 'white', marginTop: 25, padding: 10 }}>
                {/* <h1>{t('Studies')} & {t('Reports')}</h1>
                <Divider /> */}
                <List
                    itemLayout="horizontal"
                    dataSource={studies}
                    size="large"
                    renderItem={(item, index) => (
                        <List.Item>
                            <List.Item.Meta
                                title={(
                                    <div style={{ display: 'flex', flexWrap: 'wrap', flexDirection: 'column' }}>
                                        <Typography.Text style={{ fontSize: '13pt', display: 'flex', alignItems: 'start' }}>
                                            {/* <FileImageOutlined style={{ fontSize: '48px' }} /> */}
                                            <div>
                                                {new Date(item.studyDate).toLocaleDateString()} - {item.studyDescription} ({item.modality})
                                                <div style={{ display: 'flex', color: '#777777', flexDirection: 'row', flexWrap: 'wrap' }}>
                                                    {/* <div style={{ fontSize: '13pt', width: 90, }}>{t('Modality')}:</div>
                                                    <div style={{ fontSize: '13pt' }}>{item.modality}</div> */}
                                                    <div style={{ fontSize: '10pt', width: 100, marginLeft: 0 }}>{t('Image Count')}:</div>
                                                    <div style={{ fontSize: '10pt' }}>{item.numberOfStudyRelatedInstance}</div>
                                                </div>
                                                <>
                                                    <div style={{ marginTop: 8, marginLeft: 8 }}>
                                                        {
                                                            item.reports?.map(r => (
                                                                <div>
                                                                    {
                                                                        (r.confirmDate) && (
                                                                            <Typography.Link style={{ fontSize: '12pt' }}
                                                                                onClick={() => {
                                                                                    if (r.reportId && r.reportId !== 0) {
                                                                                        openRisPdf(r);
                                                                                    } else {
                                                                                        openPdf(r.fileId);
                                                                                    }
                                                                                }}
                                                                            >
                                                                                {(r.confirmDate) && (<><FilePdfOutlined style={{ fontSize: '15px', color: 'grey', marginRight: 4 }} />{new Date(r.confirmDate).toLocaleDateString()}</>)}
                                                                                &emsp;
                                                                                {r.displayName}
                                                                                &nbsp;-&nbsp;
                                                                                {r.accessionNumber}
                                                                            </Typography.Link>
                                                                        )
                                                                    }
                                                                    {
                                                                        !(r.confirmDate) && (
                                                                            <Typography.Text style={{ fontSize: '12pt' }}>
                                                                                <LoadingOutlined style={{ fontSize: '18px' }} /> {t('Waiting...')}
                                                                                &emsp;
                                                                                {r.displayName}
                                                                                &nbsp;-&nbsp;
                                                                                {r.accessionNumber}
                                                                            </Typography.Text>
                                                                        )
                                                                    }
                                                                </div>
                                                            ))
                                                        }
                                                    </div>
                                                </>
                                                <br />
                                                <div style={{ display: 'flex', color: '#777777', flexDirection: 'row', flexWrap: 'wrap' }}>
                                                    <div style={{ display: 'flex', flexWrap: 'wrap', flexDirection: isMobile ? 'column' : 'row' }}>
                                                        <Button
                                                            style={{ flex: 1, width: 'auto', display: 'flex', alignItems: 'center' }}
                                                            onClick={() => openStudy(item.studyId)}
                                                        >
                                                            <FolderOpenOutlined />
                                                            Görüntüyü Aç
                                                        </Button>
                                                        <Button
                                                            style={{ flex: 1, width: 'auto', display: 'flex', alignItems: 'center' }}
                                                            onClick={() => downloadZip(item)}
                                                        >
                                                            <CloudDownloadOutlined />
                                                            İndir
                                                        </Button>
                                                        <Button
                                                            style={{ flex: 1, width: 'auto', display: 'flex', alignItems: 'center' }}
                                                            onClick={() => getLink(item)}
                                                        >
                                                            <ShareAltOutlined />
                                                            Paylaş
                                                        </Button>
                                                        <Button
                                                            style={{ flex: 1, width: 'auto', display: 'flex', alignItems: 'center' }}
                                                            onClick={async () => {
                                                                const tokenType = await TokenDurationModal(4);
                                                                if (tokenType !== null) {
                                                                    await getOhifLink(item, tokenType);
                                                                }
                                                            }}
                                                        >
                                                            <ShareAltOutlined />
                                                            DICOM Paylaş
                                                        </Button>
                                                    </div>
                                                </div>
                                            </div>
                                        </Typography.Text>
                                    </div>
                                )}
                            />
                        </List.Item>
                    )
                    }
                />
            </div >
            <div style={{ backgroundColor: 'white', marginTop: 25, padding: 10 }}>
                <h1>{t('Guide to installing Weasis and opening a DICOM ZIP file')}</h1>

                <h2
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => toggle(1)}
                >
                    Adım 1: Weasis İndirme ve Kurulum
                </h2>
                {activeIndex === 1 && (
                    <ol style={{ marginLeft: 20 }}>
                        <button
                            style={{
                                padding: '4px 10px',
                                fontSize: '12px',
                                cursor: 'pointer',
                                borderRadius: 4,
                                border: '1px solid #007acc',
                                backgroundColor: '#e6f0fa',
                                color: '#007acc',
                                fontWeight: '600',
                                margin: '2px'
                            }}
                            onClick={() => window.open('https://github.com/nroduit/Weasis/releases/download/v4.6.2/weasis_4.6.2-1_arm64.deb', '_blank')}
                        >
                            Linux
                        </button>
                        <button
                            style={{
                                padding: '4px 10px',
                                fontSize: '12px',
                                cursor: 'pointer',
                                borderRadius: 4,
                                border: '1px solid #007acc',
                                backgroundColor: '#e6f0fa',
                                color: '#007acc',
                                fontWeight: '600',
                                margin: '2px'
                            }}
                            onClick={() => window.open('https://github.com/nroduit/Weasis/releases/download/v4.6.2/Weasis-4.6.2-x86-64.msi', '_blank')}
                        >
                            Windows
                        </button>
                        <button
                            style={{
                                padding: '4px 10px',
                                fontSize: '12px',
                                cursor: 'pointer',
                                borderRadius: 4,
                                border: '1px solid #007acc',
                                backgroundColor: '#e6f0fa',
                                color: '#007acc',
                                fontWeight: '600',
                                margin: '2px'
                            }}
                            onClick={() => window.open('https://github.com/nroduit/Weasis/releases/download/v4.6.2/Weasis-4.6.2-x86-64.pkg', '_blank')}
                        >
                            macOS
                        </button>
                        <br />
                        <li><strong>{t('Download')}:</strong> {t('Pick the right build and download it (Windows, macOS or Linux)')}</li>
                        <li><strong>{t('Run')}:</strong> {t('Run the application file.')}</li>
                        {/* <li>
                            <strong>Weasis'in Resmi Web Sitesine Gidin:</strong> Weasis'in en son
                            sürümünü indirmek için&nbsp;
                            <a
                                href="https://weasis.org/en/getting-started/index.html"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Weasis Resmi Web Sitesi
                            </a>
                            'ne gidin.
                        </li> */}
                        {/* <li style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span>
                                İndirme sayfasından uygun sürümü seçin ve indirin (Windows, macOS veya Linux için uygun olanı seçin).
                            </span>
                        </li> */}

                    </ol>
                )}

                <h2
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => toggle(2)}
                >
                    Adım 2: DICOM ZIP Dosyasını Açma
                </h2>
                {activeIndex === 2 && (
                    <ol style={{ marginLeft: 20 }}>
                        <li>
                            <strong>{t('Start Weasis')}:</strong> {t('the Weasis application on your computer')}
                            başlatın.
                        </li>
                        <li>
                            <strong>{t('Loading the DICOM ZIP file')}:</strong>
                            <ul>
                                <li>{t('Extract the zip file you downloaded into a folder.')}</li>
                                <li>{t('Once Weasis has started, drag and drop the folder onto Weasis.')}</li>
                            </ul>
                        </li>
                        <li>
                            <strong>{t('Opening the file')}:</strong>
                            <ul>
                                <li>
                                    Weasis, DICOM ZIP dosyasını açacak ve içindeki DICOM görüntülerini
                                    görüntüleyecektir.
                                </li>
                                <li>
                                    Dosyayı yükledikten sonra, DICOM görüntülerini incelemek, işlemek ve
                                    gerektiğinde kaydetmek için Weasis'in sağladığı araçları kullanabilirsiniz.
                                </li>
                            </ul>
                        </li>
                    </ol>
                )}
            </div>
        </div >
    )
}